import React, { useEffect, useState } from "react";
import { UploadCloud, LinkIcon, Loader2, X } from "lucide-react";

export default function UploadSection({
  file,
  setFile,
  videoURL,
  setVideoURL,
  loading,
  handleFileUpload,
  handleURLSubmit,
}) {
  const [previewURL, setPreviewURL] = useState(null);

  const [urlPreview, setUrlPreview] = useState(null);
  const [urlLoading, setUrlLoading] = useState(false);
  const [urlError, setUrlError] = useState(null);

  /* ---------------- FILE PREVIEW ---------------- */
  useEffect(() => {
    if (!file) {
      setPreviewURL(null);
      return;
    }

    const url = URL.createObjectURL(file);
    setPreviewURL(url);

    return () => URL.revokeObjectURL(url);
  }, [file]);

  /* ---------------- HELPERS ---------------- */

  const getYouTubeID = (url) => {
    const match = url.match(/(?:youtube\.com.*v=|youtu\.be\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  /* ---------------- AUTO YOUTUBE PREVIEW ---------------- */
  useEffect(() => {
    if (!videoURL.trim()) {
      setUrlPreview(null);
      setUrlError(null);
      return;
    }

    const delay = setTimeout(async () => {
      try {
        setUrlLoading(true);
        setUrlError(null);

        const videoId = getYouTubeID(videoURL);

        if (!videoId) {
          throw new Error("Invalid YouTube URL");
        }

        const res = await fetch(`https://noembed.com/embed?url=${videoURL}`);
        const data = await res.json();

        const thumbnail = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

        setUrlPreview({
          title: data.title,
          author: data.author_name,
          thumbnail,
        });
      } catch {
        setUrlPreview(null);
        setUrlError("Invalid YouTube URL");
      }

      setUrlLoading(false);
    }, 600);

    return () => clearTimeout(delay);
  }, [videoURL]);

  /* ---------------- CONFIRM ---------------- */
  const handleConfirm = async () => {
    await handleURLSubmit();

    // clear after submit
    setUrlPreview(null);
    setVideoURL("");
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
        {/* URL INPUT */}
        {!file && (
          <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-500">
            {/* Icon */}
            <LinkIcon className="text-gray-400 w-5 h-5 shrink-0" />

            {/* Input */}
            <input
              type="text"
              placeholder="Paste YouTube link"
              value={videoURL}
              onChange={(e) => setVideoURL(e.target.value)}
              className="w-full bg-transparent text-sm text-gray-800 placeholder-gray-400 
               !outline-none !ring-0 focus:!outline-none focus:!ring-0 
               pointer-events-auto"
            />
          </div>
        )}

        {/* LOADING */}
        {urlLoading && (
          <p className="text-sm text-gray-500">Fetching preview...</p>
        )}

        {/* ERROR */}
        {urlError && (
          <div className="text-sm text-red-500 bg-red-50 border border-red-200 p-3 rounded-lg">
            {urlError}
          </div>
        )}

        {/* PREVIEW */}
        {urlPreview && (
          <div className="border rounded-xl overflow-hidden bg-white">
            <img
              src={urlPreview.thumbnail}
              className="w-full h-52 object-cover"
              onError={(e) => {
                // fallback if maxres fails
                e.target.src = `https://img.youtube.com/vi/${getYouTubeID(
                  videoURL,
                )}/hqdefault.jpg`;
              }}
            />

            <div className="p-4 space-y-1">
              <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">
                {urlPreview.title}
              </h3>

              <p className="text-xs text-gray-500">
                {urlPreview.author} • YouTube
              </p>

              <div className="flex gap-3 mt-3">
                <button
                  onClick={() => setUrlPreview(null)}
                  className="text-xs px-3 py-1 border rounded hover:bg-gray-50"
                >
                  Cancel
                </button>

                <button
                  onClick={handleConfirm}
                  className="text-xs px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                  Confirm & Process
                </button>
              </div>
            </div>
          </div>
        )}

        {/* FILE UPLOAD */}
        {!file && (
          <>
            <label
              htmlFor="file-upload"
              className="flex items-center gap-2 cursor-pointer text-sm text-gray-500 hover:text-gray-800"
            >
              <UploadCloud className="w-4 h-4" />
              Upload Video
            </label>

            <input
              id="file-upload"
              type="file"
              className="hidden"
              accept="video/*"
              onChange={(e) => {
                const selected = e.target.files?.[0];
                if (!selected) return;
                setFile(selected);
              }}
            />
          </>
        )}

        {/* FILE PREVIEW */}
        {file && (
          <div className="relative rounded-xl overflow-hidden border">
            <video
              src={previewURL}
              controls
              className="w-full max-h-[400px] bg-black"
            />

            <button
              onClick={() => setFile(null)}
              className="absolute top-3 right-3 bg-black/70 text-white p-2 rounded-full"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* CTA */}
        {!urlPreview && (
          <button
            onClick={() => {
              if (file) handleFileUpload();
            }}
            disabled={loading}
            className="w-full bg-indigo-600 text-white py-3 rounded-xl flex justify-center items-center gap-2 hover:bg-indigo-700 transition"
          >
            {loading && <Loader2 className="animate-spin w-4 h-4" />}
            Create Clips
          </button>
        )}
      </div>
    </div>
  );
}
